import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  usePlannedExpenses,
  useExpenses,
  useCollections,
  useClassSettings,
} from "../../hooks/useData";
import { useAuth } from "../../context/AuthContext";
import { buildYearSettings, useSchoolYear } from "../../context/SchoolYearContext";
import { supabase } from "../../lib/supabase";
import {
  formatPLN,
  formatDate,
  statusColor,
  calcBalance,
  calcPlannedExpenses,
  calcForecast,
  calcToReturn,
} from "../../lib/finance";

const PLANNED_STATUS_OPTIONS = [
  "Planowane",
  "Zrealizowane",
  "Anulowane",
];

function StatCard({ label, value, sub, delay = 0, tone = "default" }) {
  const toneClasses =
    tone === "positive"
      ? "bg-gradient-to-br from-emerald-50 via-teal-50 to-amber-50"
      : "bg-white/78";

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45 }}
      className={`rounded-[28px] border border-white/70 ${toneClasses} p-5 shadow-[0_14px_40px_rgba(15,23,42,0.06)] backdrop-blur-sm`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
        {label}
      </div>
      <div className="mt-3 text-2xl font-semibold tracking-tight text-slate-900 sm:text-[1.9rem]">
        {value}
      </div>
      {sub && <div className="mt-2 text-sm leading-5 text-slate-500">{sub}</div>}
    </motion.div>
  );
}

function SectionCard({ title, subtitle, children, delay = 0 }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45 }}
      className="rounded-[30px] border border-white/70 bg-white/78 p-6 shadow-[0_18px_48px_rgba(15,23,42,0.06)] backdrop-blur-sm"
    >
      <div className="mb-5">
        <div className="text-xl font-semibold tracking-tight text-slate-900">
          {title}
        </div>
        {subtitle && (
          <div className="mt-1 text-sm leading-6 text-slate-500">{subtitle}</div>
        )}
      </div>
      {children}
    </motion.section>
  );
}

function PlannedCard({ item, index, isAdmin, onEditStatus }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.4 }}
      className="rounded-[26px] border border-slate-100 bg-slate-50/78 p-5 shadow-[0_8px_22px_rgba(15,23,42,0.04)]"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-sm font-semibold text-emerald-700 shadow-sm ring-1 ring-slate-100">
            {index + 1}
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-base font-semibold text-slate-900">
                {item.occasion ?? item.name ?? "—"}
              </div>
              <span className={`badge ${statusColor(item.status)}`}>
                {item.status ?? "Planowane"}
              </span>
            </div>

            {item.description && (
              <div className="mt-1 text-sm leading-6 text-slate-500">
                {item.description}
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              {item.planned_date && <span>Termin: {formatDate(item.planned_date)}</span>}
              {item.notes && <span>{item.notes}</span>}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 lg:items-end">
          <div className="text-right">
            <div className="text-xl font-semibold text-slate-900">
              {formatPLN(item.amount)}
            </div>
            {item.amount_per_child && (
              <div className="mt-1 text-xs text-slate-500">
                {formatPLN(item.amount_per_child)}/dziecko
              </div>
            )}
          </div>

          {isAdmin && (
            <button
              type="button"
              onClick={() => onEditStatus(item)}
              className="rounded-2xl border border-white/80 bg-white/75 px-4 py-2 text-xs font-semibold text-slate-700 shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-emerald-50 hover:text-emerald-700"
            >
              Edytuj status
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function PlannedScreen() {
  const { data: planned = [], loading: loadingP } = usePlannedExpenses();
  const { data: expenses = [], loading: loadingE } = useExpenses();
  const { data: collections = [], loading: loadingC } = useCollections();
  const { data: settings } = useClassSettings();
  const { isAdmin } = useAuth();
  const {
    selectedSchoolYear,
    selectedSchoolYearId,
    isSelectedYearActive,
    isSelectedYearDraft,
    isSelectedYearArchived,
  } = useSchoolYear();
  const canWrite = isAdmin && (isSelectedYearActive || isSelectedYearDraft);
  const yearSettings = buildYearSettings(settings, selectedSchoolYear);

  const [editingItem, setEditingItem] = useState(null);
  const [statusForm, setStatusForm] = useState("Planowane");
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const loading = loadingP || loadingE || loadingC;

  const balance = calcBalance(expenses, yearSettings, collections);
  const toReturn = calcToReturn(expenses);
  const availableFunds = balance - toReturn;
  const plannedTotal = calcPlannedExpenses(planned);
  const { sufficient, surplus } = calcForecast(availableFunds, plannedTotal);

  const pending = planned.filter(
    (p) => p.status !== "Zrealizowane" && p.status !== "Anulowane"
  );
  const done = planned.filter((p) => p.status === "Zrealizowane");
  const canceled = planned.filter((p) => p.status === "Anulowane");

  useEffect(() => {
    if (editingItem) {
      setStatusForm(editingItem.status ?? "Planowane");
      setStatusError("");
      setStatusMessage("");
    } else {
      setStatusForm("Planowane");
      setStatusError("");
      setStatusMessage("");
    }
  }, [editingItem]);

  function openEditStatus(item) {
    if (!canWrite) return;
    setEditingItem(item);
  }

  function closeEditStatus() {
    if (statusSubmitting) return;
    setEditingItem(null);
  }

  async function handleStatusSubmit(e) {
    e.preventDefault();
    setStatusError("");
    setStatusMessage("");

    if (!editingItem) return;

    if (!canWrite) {
      setStatusError("Rok tylko do odczytu.");
      return;
    }

    try {
      setStatusSubmitting(true);

      const { error } = await supabase
        .from("planned_expenses")
        .update({
          status: statusForm,
        })
        .eq("id", editingItem.id)
        .eq("school_year_id", selectedSchoolYearId);

      if (error) throw error;

      setStatusMessage("Status planowanego wydatku został zaktualizowany.");

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      setStatusError(error.message || "Nie udało się zaktualizować statusu.");
    } finally {
      setStatusSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!editingItem) return;
    if (!canWrite) {
      setStatusError("Rok tylko do odczytu.");
      return;
    }

    const confirmed = window.confirm(
      `Czy na pewno chcesz usunąć planowany wydatek "${editingItem.occasion ?? editingItem.name ?? "—"}"?`
    );

    if (!confirmed) return;

    setStatusError("");
    setStatusMessage("");

    try {
      setStatusSubmitting(true);

      const { error } = await supabase
        .from("planned_expenses")
        .delete()
        .eq("id", editingItem.id)
        .eq("school_year_id", selectedSchoolYearId);

      if (error) throw error;

      setStatusMessage("Planowany wydatek został usunięty.");

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      setStatusError(error.message || "Nie udało się usunąć planowanego wydatku.");
    } finally {
      setStatusSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.82)_0%,rgba(236,253,245,0.72)_45%,rgba(255,247,237,0.76)_100%)] px-6 py-7 shadow-[0_18px_52px_rgba(15,23,42,0.06)]"
      >
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700/80">
            Finanse
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Planowane wydatki
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            Podgląd nadchodzących kosztów, statusów realizacji oraz wpływu planów na dostępne środki.
          </p>
        </div>
      </motion.div>

      {isAdmin && isSelectedYearDraft && (
        <SectionCard
          title="Wersja robocza"
          subtitle="Dane mogą edytować wyłącznie administratorzy."
          delay={0.18}
        >
          <div className="text-sm font-medium text-sky-800">
            Wersja robocza roku szkolnego. Dane mogą edytować wyłącznie administratorzy.
          </div>
        </SectionCard>
      )}

      {isAdmin && isSelectedYearArchived && (
        <SectionCard
          title="Rok tylko do odczytu"
          subtitle="Edycja statusów i usuwanie planowanych wydatków są wyłączone."
          delay={0.18}
        >
          <div className="text-sm font-medium text-amber-800">Rok tylko do odczytu.</div>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          label="Dostępne środki"
          value={formatPLN(availableFunds)}
          sub="Po odjęciu kwot do zwrotu"
          delay={0.08}
          tone="positive"
        />
        <StatCard
          label="Suma planowanych"
          value={formatPLN(plannedTotal)}
          sub="Łączna wartość wszystkich planowanych wydatków"
          delay={0.12}
        />
        <StatCard
          label="Saldo po planowanych"
          value={formatPLN(surplus)}
          sub={sufficient ? "Środki wystarczające" : "Niedobór środków"}
          delay={0.16}
          tone={sufficient ? "positive" : "default"}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.45 }}
        className={`rounded-[28px] border px-5 py-4 shadow-[0_14px_36px_rgba(15,23,42,0.05)] backdrop-blur-sm ${
          sufficient
            ? "border-emerald-200 bg-emerald-50/85"
            : "border-red-200 bg-red-50/85"
        }`}
      >
        <div className="flex items-start gap-4">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-lg ${
              sufficient
                ? "bg-emerald-100 text-emerald-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {sufficient ? "✓" : "⚠"}
          </div>

          <div>
            <div
              className={`text-sm font-semibold ${
                sufficient ? "text-emerald-800" : "text-red-800"
              }`}
            >
              {sufficient ? "Środki wystarczające" : "Niedobór środków"}
            </div>
            <div
              className={`mt-1 text-sm ${
                sufficient ? "text-emerald-700/90" : "text-red-700/90"
              }`}
            >
              {sufficient
                ? `Po planowanych wydatkach zostanie nadwyżka: ${formatPLN(surplus)}`
                : `Brakuje ${formatPLN(Math.abs(surplus))} na pokrycie planowanych wydatków`}
            </div>
          </div>
        </div>
      </motion.div>

      {pending.length > 0 && (
        <SectionCard
          title={`Nadchodzące (${pending.length})`}
          subtitle="Pozycje, które jeszcze nie zostały zrealizowane"
          delay={0.24}
        >
          <div className="space-y-3">
            {pending.map((item, i) => (
              <PlannedCard
                key={item.id}
                item={item}
                index={i}
                isAdmin={canWrite}
                onEditStatus={openEditStatus}
              />
            ))}
          </div>
        </SectionCard>
      )}

      {done.length > 0 && (
        <SectionCard
          title={`Zrealizowane (${done.length})`}
          subtitle="Pozycje już wykonane"
          delay={0.3}
        >
          <div className="space-y-3">
            {done.map((item, i) => (
              <PlannedCard
                key={item.id}
                item={item}
                index={i}
                isAdmin={canWrite}
                onEditStatus={openEditStatus}
              />
            ))}
          </div>
        </SectionCard>
      )}

      {canceled.length > 0 && (
        <SectionCard
          title={`Anulowane (${canceled.length})`}
          subtitle="Pozycje oznaczone jako anulowane"
          delay={0.36}
        >
          <div className="space-y-3">
            {canceled.map((item, i) => (
              <PlannedCard
                key={item.id}
                item={item}
                index={i}
                isAdmin={canWrite}
                onEditStatus={openEditStatus}
              />
            ))}
          </div>
        </SectionCard>
      )}

      {planned.length === 0 && (
        <SectionCard
          title="Brak planowanych wydatków"
          subtitle="Nie dodano jeszcze żadnych pozycji."
          delay={0.18}
        >
          <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-10 text-center text-sm text-slate-500">
            Brak planowanych wydatków do wyświetlenia.
          </div>
        </SectionCard>
      )}

      <EditPlannedStatusModal
        open={!!editingItem}
        item={editingItem}
        statusForm={statusForm}
        setStatusForm={setStatusForm}
        submitting={statusSubmitting}
        error={statusError}
        message={statusMessage}
        onClose={closeEditStatus}
        onSubmit={handleStatusSubmit}
        onDelete={handleDelete}
        isAdmin={canWrite}
      />
    </div>
  );
}

function EditPlannedStatusModal({
  open,
  item,
  statusForm,
  setStatusForm,
  submitting,
  error,
  message,
  onClose,
  onSubmit,
  onDelete,
  isAdmin,
}) {
  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-[30px] border border-white/70 bg-white/95 p-6 shadow-[0_24px_70px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xl font-semibold tracking-tight text-slate-900">
              Edytuj status planowanego wydatku
            </div>
            <div className="mt-1 text-sm text-slate-500">
              Zmień status albo usuń pozycję całkowicie.
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100/80 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        <div className="mt-5 rounded-[24px] border border-slate-100 bg-slate-50/80 p-4 space-y-2">
          <div className="text-sm font-medium text-slate-900">
            {item.occasion ?? item.name ?? "—"}
          </div>

          {item.description && (
            <div className="text-sm leading-6 text-slate-500">{item.description}</div>
          )}

          <div className="text-sm text-slate-500">
            Kwota: <span className="font-medium text-slate-800">{formatPLN(item.amount)}</span>
          </div>

          {item.planned_date && (
            <div className="text-sm text-slate-500">
              Data: <span className="font-medium text-slate-700">{formatDate(item.planned_date)}</span>
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="mt-5 space-y-4">
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
              Status
            </label>
            <select
              value={statusForm}
              onChange={(e) => setStatusForm(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition-colors focus:border-emerald-300"
            >
              {PLANNED_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {message && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {message}
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              {isAdmin && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={submitting}
                  className="rounded-2xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
                >
                  {submitting ? "Usuwanie..." : "Usuń całkowicie"}
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                Anuluj
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(16,185,129,0.22)] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
              >
                {submitting ? "Zapisywanie..." : "Zapisz status"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
